import type { ProfileFieldResponse, ProfileSectionResponse } from "@services/facilityManagement";

/** Federal tax statuses treated as IRC tax-exempt orgs for Title 26 profile UI (matches unicomply-rules). */
export const TITLE26_TAX_EXEMPT_ENTITY_STATUSES = new Set<string>([
  "nonprofit_501c3_public_charity",
  "nonprofit_501c3_hospital",
  "nonprofit_private_foundation",
  "government_tribal_public",
  "nonprofit_other",
]);

const EXEMPT_ONLY_FIELD_KEYS = new Set<string>([
  "exempt_gross_receipts_band",
  "has_unrelated_business_income",
  "unrelated_business_income_amount",
  "state_exemption_or_charitable_registration_required",
]);

function unwrapProfileValue(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "object" && v !== null && "value" in v) {
    const inner = (v as { value: unknown }).value;
    if (inner === null || inner === undefined) return null;
    const s = String(inner).trim();
    return s || null;
  }
  const s = String(v).trim();
  return s || null;
}

/** Apply entity-type-aware visibility and FYE labels for the Tax profile section (draft-aware). */
export function applyTitle26TaxFieldOverrides(
  field: ProfileFieldResponse,
  entityTaxStatusRaw: unknown,
): ProfileFieldResponse {
  const status = (unwrapProfileValue(entityTaxStatusRaw) ?? "").trim();
  const showExemptOnly = status !== "" && TITLE26_TAX_EXEMPT_ENTITY_STATUSES.has(status);

  if (field.field_key === "fiscal_year_end") {
    if (showExemptOnly) {
      return {
        ...field,
        label: "Fiscal year end (tax / Form 990)",
        help_text:
          "End of the tax or financial reporting year. Drives Form 990-series due dates " +
          "and extension timing when applicable.",
      };
    }
    return {
      ...field,
      label: "Fiscal year end (tax reporting)",
      help_text:
        "End of the organization's tax year. If blank, analysis assumes a calendar year " +
        "ending December 31.",
    };
  }

  if (EXEMPT_ONLY_FIELD_KEYS.has(field.field_key)) {
    if (!showExemptOnly) {
      return { ...field, visible: false, is_required: false };
    }
  }

  return field;
}

export function mergeTaxSectionForDisplay(
  section: ProfileSectionResponse,
  entityTaxStatusRaw: unknown,
): ProfileSectionResponse {
  if (section.section_key !== "tax") return section;
  return {
    ...section,
    fields: section.fields.map((f) => applyTitle26TaxFieldOverrides(f, entityTaxStatusRaw)),
  };
}
